'use client';

/**
 * Design Intent Canvas Widget (multi-view)
 *
 * @component_role Canvas widget for `design_intent_graph` canvas strategy
 * @business_rule Multi-view widget following the Obeya pattern (view tabs) +
 *   Testing pattern (node-type filters on the graph view).
 *
 *   Views:
 *   - Graph: Actor-centric 2-hop Reagraph with filterable node types
 *   - Gallery: DI reference images in a larger visual grid
 *
 *   Data:
 *   - Graph view: `graph_design_intent_visualize` BFF strategy
 *   - Gallery view: `graph_design_intent_explore` BFF strategy (media_urls)
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain discovery
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Network,
  Image as ImageIcon,
  ZoomIn,
  ZoomOut,
  Maximize2,
  MousePointer2,
  MessageSquare,
  Lightbulb,
  Loader2 as Spinner,
  TrendingDown,
  Target,
  AlertTriangle,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { cn } from '@/lib/utils/cn';
import type { GraphNode as APIGraphNode, GraphEdge as APIGraphEdge } from '@/lib/api/types';

// ============================================================
// View modes
// ============================================================
type ViewMode = 'intelligence' | 'graph' | 'conversations';

// ============================================================
// Graph styling
// ============================================================
const NODE_COLORS: Record<string, string> = {
  actor: '#E91E63',
  company: '#E91E63',
  designintent: '#F5A623',
  semanticprofile: '#7ED321',
  media: '#9B9B9B',
  project: '#4A90D9',
  product: '#4DD0E1',
  rfq: '#F5A623',
  po: '#7ED321',
  mcp: '#F59E0B',
};

const NODE_SIZES: Record<string, number> = {
  actor: 16,
  company: 14,
  designintent: 12,
  semanticprofile: 8,
  media: 6,
  project: 10,
  product: 8,
  rfq: 10,
  po: 10,
  mcp: 10,
};

// Types the user can toggle on/off in the graph view
const FILTERABLE_TYPES = ['product', 'project', 'rfq', 'po', 'media', 'semanticprofile'] as const;
const DEFAULT_HIDDEN = new Set<string>(['media', 'semanticprofile']);

// ============================================================
// Graph View
// ============================================================
function GraphView({
  selectedId,
  L,
}: {
  selectedId: string | null;
  L: Record<string, string>;
}) {
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);
  const { resolvedTheme } = useTheme();
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const graphRef = useRef<any>(null);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);

  // Node-type filters (Testing pattern)
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set(DEFAULT_HIDDEN));
  const toggleType = useCallback((type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const strategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'design_intent_visualize'),
  );

  const { data, isLoading, error } = useStrategyData<{
    nodes: APIGraphNode[];
    edges: APIGraphEdge[];
    metadata: Record<string, unknown>;
  }>(
    strategy,
    selectedId ? { semantic_id: selectedId } : {},
    !!strategy && !!selectedId,
  );

  const graphData = data?.data;

  // Load Reagraph client-side
  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);
      setThemes({
        dark: {
          ...mod.darkTheme,
          canvas: { ...mod.darkTheme.canvas, background: '#121212' },
          node: { ...mod.darkTheme.node, activeFill: '#ffffff', inactiveOpacity: 0.3 },
          edge: { ...mod.darkTheme.edge, activeFill: '#ffffff', inactiveOpacity: 0.2 },
        },
        light: mod.lightTheme,
      });
    });
  }, []);

  // Map + filter nodes/edges
  const { nodes, edges, filteredCounts } = useMemo(() => {
    if (!graphData?.nodes) return { nodes: [], edges: [], filteredCounts: { visible: 0, hidden: 0 } };

    const visibleNodes = graphData.nodes.filter((n) => !hiddenTypes.has(n.type));
    const visibleIds = new Set(visibleNodes.map((n) => n.id));

    const nodes = visibleNodes.map((n) => ({
      id: n.id,
      label: n.label,
      fill: NODE_COLORS[n.type] ?? '#666666',
      size: NODE_SIZES[n.type] ?? 8,
      cluster: n.type,
      data: { type: n.type, extra: n.extra },
    }));

    const edges = graphData.edges
      .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
      .map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.type.replace(/_/g, ' '),
      }));

    return {
      nodes,
      edges,
      filteredCounts: {
        visible: visibleNodes.length,
        hidden: graphData.nodes.length - visibleNodes.length,
      },
    };
  }, [graphData, hiddenTypes]);

  const handleNodeClick = useCallback(
    (node: any) => {
      setSelectedNodes([node.id]);
      setActiveNodes([node.id]);
      const nodeData = graphData?.nodes?.find((n) => n.id === node.id);
      if (nodeData) {
        selectNode({
          id: nodeData.id,
          label: nodeData.label,
          type: nodeData.type,
        });
      }
    },
    [graphData, selectNode],
  );

  const handleZoomIn = () => graphRef.current?.zoomIn?.();
  const handleZoomOut = () => graphRef.current?.zoomOut?.();
  const handleFitView = () => graphRef.current?.fitNodesInView?.();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-8 h-8 mb-3 animate-spin" />
        <p className="text-sm">{L.graph_loading ?? 'Loading graph...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <AlertCircle className="w-8 h-8 mb-3 text-destructive" />
        <p className="text-sm text-destructive">{error.message}</p>
      </div>
    );
  }

  if (!isClient || !GraphCanvas || !themes || nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-40" />
        <p className="text-sm">{L.graph_empty ?? 'No graph data'}</p>
      </div>
    );
  }

  const theme = resolvedTheme === 'dark' ? themes.dark : themes.light;

  return (
    <div className="flex flex-col h-full">
      {/* Filter toolbar */}
      <div className="flex items-center gap-2 px-4 py-1.5 border-b border-panel-border bg-panel text-xs">
        {/* Type filter pills */}
        <div className="flex items-center gap-1">
          {FILTERABLE_TYPES.map((type) => {
            const isHidden = hiddenTypes.has(type);
            const color = NODE_COLORS[type];
            return (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors',
                  isHidden
                    ? 'bg-muted/50 text-muted-foreground/50 line-through'
                    : 'bg-muted text-foreground',
                )}
                title={`${isHidden ? 'Show' : 'Hide'} ${type} nodes`}
              >
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: color, opacity: isHidden ? 0.3 : 1 }}
                />
                <span className="capitalize">{type.replace('semanticprofile', 'profiles').replace('designintent', 'intents')}</span>
              </button>
            );
          })}
        </div>

        {/* Zoom controls */}
        <div className="flex items-center border-l border-panel-border pl-2 ml-auto">
          <button onClick={handleZoomIn} className="p-1 rounded hover:bg-muted" title="Zoom in">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleZoomOut} className="p-1 rounded hover:bg-muted" title="Zoom out">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleFitView} className="p-1 rounded hover:bg-muted" title="Fit to view">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Counts */}
        <div className="flex items-center gap-2 text-muted-foreground border-l border-panel-border pl-2">
          <span>{nodes.length} nodes</span>
          <span>{edges.length} edges</span>
          {filteredCounts.hidden > 0 && (
            <span className="text-muted-foreground/50">({filteredCounts.hidden} hidden)</span>
          )}
        </div>
      </div>

      {/* Reagraph canvas */}
      <div className="flex-1 relative overflow-hidden" style={{ minHeight: '300px' }}>
        <div className="absolute inset-0">
          <GraphCanvas
            ref={graphRef}
            nodes={nodes}
            edges={edges}
            theme={theme}
            layoutType="forceDirected2d"
            labelType="nodes"
            draggable
            animated
            cameraMode="pan"
            edgeLabelPosition="above"
            selections={selectedNodes}
            actives={activeNodes}
            onNodeClick={handleNodeClick}
          />
        </div>
      </div>

      {/* Legend bar */}
      <div className="flex items-center justify-between px-4 py-1.5 border-t border-panel-border bg-panel text-xs">
        <div className="flex items-center gap-3">
          {Object.entries(NODE_COLORS).map(([type, color]) => {
            const isFilterable = (FILTERABLE_TYPES as readonly string[]).includes(type);
            const isHidden = hiddenTypes.has(type);
            return (
              <div
                key={type}
                className={cn(
                  'flex items-center gap-1',
                  isFilterable && 'cursor-pointer hover:opacity-80',
                )}
                onClick={isFilterable ? () => toggleType(type) : undefined}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: color, opacity: isHidden ? 0.3 : 1 }}
                />
                <span
                  className={cn(
                    'capitalize',
                    isHidden ? 'text-muted-foreground/40 line-through' : 'text-muted-foreground',
                  )}
                >
                  {type.replace('designintent', 'intent').replace('semanticprofile', 'profile').replace('purchaseorder', 'PO')}
                </span>
              </div>
            );
          })}
        </div>
        <div className="text-muted-foreground">
          <MousePointer2 className="w-3 h-3 inline mr-1" />
          Click to select
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Gallery View — reference images in a larger grid
// ============================================================
function GalleryView({
  selectedId,
  L,
}: {
  selectedId: string | null;
  L: Record<string, string>;
}) {
  const strategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'design_intent_explore'),
  );

  const { data, isLoading } = useStrategyData<{
    design_intent: {
      name: string;
      brief: string;
      intent_description: string | null;
      media_urls: string[];
    } | null;
  }>(
    strategy,
    selectedId ? { semantic_id: selectedId } : {},
    !!strategy && !!selectedId,
  );

  const di = data?.data?.design_intent;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!di || !di.media_urls?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <ImageIcon className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm">{L.gallery_empty ?? 'No reference images'}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4">
      {/* Title + brief */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold">{di.name}</h2>
        {di.brief && <p className="text-sm text-muted-foreground mt-1">{di.brief}</p>}
        {di.intent_description && (
          <p className="text-sm text-muted-foreground/80 mt-2 italic">{di.intent_description}</p>
        )}
      </div>

      {/* Image grid */}
      <div className="grid grid-cols-2 gap-3">
        {di.media_urls.map((url, idx) => (
          <div key={idx} className="aspect-square bg-muted rounded-lg overflow-hidden border border-border">
            <img
              src={url}
              alt={`${di.name} reference ${idx + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Conversations View — search sessions that used this DI
// ============================================================

interface DIConversation {
  conversation_sid: string;
  query: string | null;
  turn_count: number;
  created_at: string | null;
}

function ConversationsView({ selectedId, L }: { selectedId: string; L: Record<string, string> }) {
  const exploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'design_intent_explore'));
  const { data, isLoading } = useStrategyData<{ conversations: DIConversation[] }>(
    exploreStrategy,
    selectedId ? { semantic_id: selectedId } : {},
    !!exploreStrategy && !!selectedId,
  );
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  const conversations: DIConversation[] = data?.data?.conversations ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!conversations.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <MessageSquare className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">{L.canvas_conversations_empty}</p>
      </div>
    );
  }

  return (
    <div className="overflow-auto p-4">
      <div className="space-y-2">
        {conversations.map((cv) => (
          <button
            key={cv.conversation_sid}
            onClick={() => {
              selectNode({
                id: selectedId,
                label: cv.query || cv.conversation_sid,
                type: 'design_intent',
              });
            }}
            className={cn(
              'w-full text-left p-3 rounded-lg border border-panel-border',
              'hover:border-primary/50 hover:bg-accent/30 transition-colors',
            )}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium truncate">
                {cv.query || cv.conversation_sid}
              </span>
              <span className="text-[10px] font-mono text-muted-foreground shrink-0 ml-2">
                {(L.canvas_conversation_turns || '').replace('{n}', String(cv.turn_count))}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span className="font-mono">{cv.conversation_sid}</span>
              {cv.created_at && (
                <>
                  <span>·</span>
                  <span>{new Date(cv.created_at).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Intelligence View — report cards
// ============================================================

function IntelligenceView({ selectedId, L }: { selectedId: string; L: Record<string, string> }) {
  const [productSids, setProductSids] = useState<string[]>([]);

  return (
    <div className="h-full overflow-auto p-4 space-y-4">
      <AlignmentCard selectedId={selectedId} L={L} />
      <ArbitrageCard L={L} onProductsReady={setProductSids} />
      <ParetoChart L={L} />
      {productSids.length > 0 && <ProductStrip sids={productSids} L={L} />}
    </div>
  );
}

function AlignmentCard({ selectedId, L }: { selectedId: string; L: Record<string, string> }) {
  const lang = useLanguageStore((s) => s.lang);
  const strategy = useCapabilityStore((s) => s.strategyName('discovery', 'alignment_summary'));
  const { data, isLoading } = useStrategyData<{
    summary: string;
    intent_name: string;
    project_name: string;
    influenced_pct: number;
    rfq_products: number;
    missed_count: number;
    total_potential_saving: number;
  }>(
    strategy ?? 'di_alignment_summary',
    selectedId ? { intent_sid: selectedId, lang } : {},
    !!selectedId,
  );

  const result = data?.data;

  return (
    <div className="rounded-lg border border-panel-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border/50">
        <Target className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold">{L.canvas_intel_alignment}</h3>
        {result?.project_name && (
          <span className="text-[10px] text-muted-foreground ml-auto">{result.project_name}</span>
        )}
      </div>
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="w-4 h-4 animate-spin" />
            {L.canvas_intel_loading}
          </div>
        ) : !result?.summary || !result?.project_name ? (
          <p className="text-sm text-muted-foreground">{L.canvas_intel_no_project}</p>
        ) : (
          <>
            <p className="text-sm leading-relaxed mb-3">{result.summary}</p>
            <div className="flex gap-4 pt-2 border-t border-border/50">
              <div>
                <p className="text-lg font-bold">{result.influenced_pct}%</p>
                <p className="text-[10px] text-muted-foreground">{L.canvas_intel_di_influenced}</p>
              </div>
              <div>
                <p className="text-lg font-bold">{result.rfq_products}</p>
                <p className="text-[10px] text-muted-foreground">{L.canvas_intel_products}</p>
              </div>
              <div>
                <p className="text-lg font-bold">{result.missed_count}</p>
                <p className="text-[10px] text-muted-foreground">{L.canvas_intel_missed}</p>
              </div>
              {result.total_potential_saving > 0 && (
                <div>
                  <p className="text-lg font-bold text-amber-600">
                    ${result.total_potential_saving.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{L.canvas_intel_potential_saving}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ArbitrageCard({ L, onProductsReady }: { L: Record<string, string>; onProductsReady?: (sids: string[]) => void }) {
  const lang = useLanguageStore((s) => s.lang);
  const strategy = useCapabilityStore((s) => s.strategyName('discovery', 'arbitrage_summary'));
  const { data, isLoading } = useStrategyData<{
    summary: string;
    di_count: number;
    di_names: string[];
    opportunities_count: number;
    max_spread_type: string | null;
    max_spread_pct: number;
    pairs: Array<{
      product_type: string;
      cheapest: { sid: string; name: string; price: number; di_score: number; quality?: string | null };
      most_expensive: { sid: string; name: string; price: number; di_score: number; quality?: string | null };
      spread: number;
      saving_pct: number;
    }>;
  }>(
    strategy ?? 'di_arbitrage_summary',
    { lang },
    true,
  );

  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const result = data?.data;

  useEffect(() => {
    if (result?.pairs?.length && onProductsReady) {
      const sids = result.pairs.map((p) => p.cheapest.sid);
      onProductsReady([...new Set(sids)].slice(0, 5));
    }
  }, [result?.pairs, onProductsReady]);

  return (
    <div className="rounded-lg border border-panel-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border/50">
        <TrendingDown className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-semibold">{L.canvas_intel_arbitrage}</h3>
        {result?.di_names?.length ? (
          <span className="text-[10px] text-muted-foreground ml-auto truncate max-w-[200px]">
            {result.di_names.join(', ')}
          </span>
        ) : null}
      </div>
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="w-4 h-4 animate-spin" />
            {L.canvas_intel_loading}
          </div>
        ) : !result?.summary ? (
          <p className="text-sm text-muted-foreground">{L.canvas_intel_no_data}</p>
        ) : (
          <>
            <p className="text-sm leading-relaxed mb-3">{result.summary}</p>

            {/* Arbitrage pairs — name + price + score, two columns per row */}
            {result.pairs?.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {result.pairs.map((pair) => (
                  <div key={pair.product_type} className="grid grid-cols-[1fr_auto_1fr] gap-1 items-start text-[11px]">
                    <button
                      onClick={() => inspectEntity({ id: pair.cheapest.sid, label: pair.cheapest.name, type: 'product' })}
                      className="text-left hover:text-primary transition-colors border-l-2 border-green-500 pl-1.5 min-w-0"
                    >
                      <p className="truncate font-medium">{pair.cheapest.name}</p>
                      <p><span className="font-mono text-green-600">${pair.cheapest.price.toLocaleString()}</span> <span className="text-muted-foreground">{pair.cheapest.di_score.toFixed(2)}</span>{pair.cheapest.quality && <span className="text-muted-foreground/70"> · {pair.cheapest.quality}</span>}</p>
                    </button>
                    <span className="text-muted-foreground self-center text-[10px]">→</span>
                    <button
                      onClick={() => inspectEntity({ id: pair.most_expensive.sid, label: pair.most_expensive.name, type: 'product' })}
                      className="text-left hover:text-primary transition-colors border-l-2 border-amber-500 pl-1.5 min-w-0"
                    >
                      <p className="truncate font-medium">{pair.most_expensive.name}</p>
                      <p><span className="font-mono text-amber-600">${pair.most_expensive.price.toLocaleString()}</span> <span className="text-muted-foreground">{pair.most_expensive.di_score.toFixed(2)}</span>{pair.most_expensive.quality && <span className="text-muted-foreground/70"> · {pair.most_expensive.quality}</span>}</p>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-4 pt-2 border-t border-border/50">
              <div>
                <p className="text-lg font-bold">{result.opportunities_count}</p>
                <p className="text-[10px] text-muted-foreground">{L.canvas_intel_opportunities}</p>
              </div>
              {result.max_spread_type && (
                <div>
                  <p className="text-lg font-bold text-amber-600">{result.max_spread_pct}%</p>
                  <p className="text-[10px] text-muted-foreground">
                    {(L.canvas_intel_max_spread || '').replace('{type}', result.max_spread_type)}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Pareto Chart — price × DI score scatter plot
// ============================================================

interface ParetoProduct {
  sid: string;
  name: string;
  price: number;
  di_score: number;
  product_type: string | null;
  quality: string | null;
  on_frontier: boolean;
}

function ParetoChart({ L }: { L: Record<string, string> }) {
  const strategy = useCapabilityStore((s) => s.strategyName('discovery', 'arbitrage'));
  const { data, isLoading } = useStrategyData<{
    all_products: ParetoProduct[];
  }>(
    strategy ?? 'di_arbitrage',
    {},
    true,
  );
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const [hoveredSid, setHoveredSid] = useState<string | null>(null);

  const products = data?.data?.all_products ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Spinner className="w-4 h-4 animate-spin" />
        {L.canvas_intel_loading}
      </div>
    );
  }

  if (products.length < 2) return null;

  const prices = products.map((p) => p.price);
  const scores = products.map((p) => p.di_score);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const priceRange = maxPrice - minPrice || 1;
  const scoreRange = maxScore - minScore || 0.01;

  const hovered = hoveredSid ? products.find((p) => p.sid === hoveredSid) : null;

  return (
    <div className="rounded-lg border border-panel-border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border/50">
        <TrendingDown className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold">{L.canvas_intel_pareto}</h3>
        <span className="text-[10px] text-muted-foreground ml-auto">{products.length} products</span>
      </div>
      <div className="p-4">
        {/* Scatter plot */}
        <div className="relative w-full aspect-[2/1] bg-muted/20 rounded border border-border/30">
          {/* Y-axis label */}
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 -rotate-90 text-[9px] text-muted-foreground whitespace-nowrap">
            {L.canvas_intel_pareto_score}
          </div>
          {/* X-axis label */}
          <div className="absolute bottom-[-18px] left-1/2 -translate-x-1/2 text-[9px] text-muted-foreground">
            {L.canvas_intel_pareto_price}
          </div>
          {/* Dots */}
          {products.map((p) => {
            const x = ((p.price - minPrice) / priceRange) * 90 + 5; // 5-95%
            const y = (1 - (p.di_score - minScore) / scoreRange) * 85 + 5; // inverted, 5-90%
            const isHovered = hoveredSid === p.sid;
            return (
              <button
                key={p.sid}
                onMouseEnter={() => setHoveredSid(p.sid)}
                onMouseLeave={() => setHoveredSid(null)}
                onClick={() => inspectEntity({ id: p.sid, label: p.name, type: 'product' })}
                className={cn(
                  'absolute rounded-full transition-all',
                  p.on_frontier
                    ? 'bg-primary border-2 border-primary z-10'
                    : 'bg-muted-foreground/30 border border-muted-foreground/20',
                  isHovered ? 'scale-150 z-20' : '',
                )}
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  width: p.on_frontier ? 10 : 6,
                  height: p.on_frontier ? 10 : 6,
                  transform: `translate(-50%, -50%)${isHovered ? ' scale(1.5)' : ''}`,
                }}
              />
            );
          })}
        </div>
        {/* Tooltip */}
        {hovered && (
          <div className="mt-3 text-xs flex items-center gap-3">
            <span className={cn('w-2 h-2 rounded-full', hovered.on_frontier ? 'bg-primary' : 'bg-muted-foreground/30')} />
            <span className="font-medium truncate flex-1">{hovered.name}</span>
            <span className="font-mono">${hovered.price.toLocaleString()}</span>
            <span className="text-muted-foreground">{hovered.di_score.toFixed(2)}</span>
            {hovered.quality && <span className="text-muted-foreground/70 text-[10px]">{hovered.quality}</span>}
            {hovered.on_frontier && <span className="text-primary text-[10px] font-semibold">{L.canvas_intel_pareto_frontier}</span>}
          </div>
        )}
        {/* Legend */}
        <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-primary" /> {L.canvas_intel_pareto_optimal}</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" /> {L.canvas_intel_pareto_dominated}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Product Strip — highlighted products with images
// ============================================================

interface StripProduct {
  sid: string;
  name: string;
  price: number | null;
  image_url: string | null;
}

function ProductStrip({ sids, L }: { sids: string[]; L: Record<string, string> }) {
  const [products, setProducts] = useState<StripProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const productStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'product_explore'));

  useEffect(() => {
    if (!sids.length || !productStrategy) return;
    let cancelled = false;
    setLoading(true);

    // Fetch each product — small N (max 5), parallel
    const fetchAll = async () => {
      const { api } = await import('@/lib/api/client');
      const results = await Promise.allSettled(
        sids.map((sid) =>
          api.getStrategyData<{ product: Record<string, unknown> }>(
            productStrategy, { semantic_id: sid },
          ),
        ),
      );
      if (cancelled) return;
      const prods: StripProduct[] = [];
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value.success) {
          const p = r.value.data?.product;
          if (p) {
            const images = (p.images ?? {}) as Record<string, unknown>;
            const gallery = (images.gallery_urls ?? []) as string[];
            prods.push({
              sid: p.semantic_id as string,
              name: p.name as string,
              price: p.list_price as number | null,
              image_url: (gallery[0] ?? images.primary ?? null) as string | null,
            });
          }
        }
      }
      setProducts(prods);
      setLoading(false);
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [sids.join(','), productStrategy]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Spinner className="w-4 h-4 animate-spin" />
        {L.canvas_intel_loading}
      </div>
    );
  }

  if (!products.length) return null;

  return (
    <div className="border-t border-border pt-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {L.canvas_intel_products}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {products.map((p) => (
          <button
            key={p.sid}
            onClick={() => inspectEntity({ id: p.sid, label: p.name, type: 'product' })}
            className={cn(
              'group relative rounded-lg border border-panel-border overflow-hidden cursor-pointer',
              'hover:border-primary/50 transition-colors text-left',
            )}
          >
            <div className="aspect-square bg-muted relative">
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt={p.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="p-2">
              <h4 className="font-medium text-xs truncate group-hover:text-primary transition-colors">
                {p.name}
              </h4>
              {p.price != null && (
                <p className="text-xs text-muted-foreground">${p.price.toLocaleString()}</p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Main Widget — multi-view wrapper (Obeya pattern)
// ============================================================
interface DesignIntentGraphWidgetProps {
  default_view?: string;
}

export function DesignIntentGraphWidget({ default_view }: DesignIntentGraphWidgetProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(
    (default_view as ViewMode) ?? 'intelligence',
  );
  const selectedId = useGraphSelectionStore((s) => s.selectedNode?.id ?? null);
  const selectedLabel = useGraphSelectionStore((s) => s.selectedNode?.label ?? null);
  const L = useExplorerLabels('discovery');

  if (!selectedId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-40" />
        <p className="text-sm">{L.canvas_select_style}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header with view tabs (Obeya pattern) */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 bg-muted/30">
        {/* Breadcrumb */}
        <div className="text-sm text-muted-foreground truncate">
          <span className="font-medium text-foreground">{selectedLabel}</span>
        </div>

        {/* View toggle: Intelligence | Graph | Searches */}
        <div className="flex items-center bg-muted/50 rounded-md p-0.5">
          <button
            onClick={() => setViewMode('intelligence')}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
              viewMode === 'intelligence'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Lightbulb className="w-3.5 h-3.5" />
            {L.canvas_view_intelligence}
          </button>
          <button
            onClick={() => setViewMode('graph')}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
              viewMode === 'graph'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Network className="w-3.5 h-3.5" />
            {L.canvas_view_graph_di}
          </button>
          <button
            onClick={() => setViewMode('conversations')}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors',
              viewMode === 'conversations'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {L.canvas_view_conversations}
          </button>
        </div>
      </div>

      {/* View content */}
      <div className="flex-1 min-h-0">
        {viewMode === 'intelligence' && <IntelligenceView selectedId={selectedId} L={L} />}
        {viewMode === 'graph' && <GraphView selectedId={selectedId} L={L} />}
        {viewMode === 'conversations' && <ConversationsView selectedId={selectedId} L={L} />}
      </div>
    </div>
  );
}
